import { useEffect } from "react";

import {
  getWorkspaceLocalePreferences,
  listAvailableLocales,
} from "./localeApi";
import {
  applyRuntimeLocale,
  FALLBACK_LOCALE,
  resolveRuntimeLocale,
} from "./localeRegistry";

const AUTHENTICATED_PREFIXES = [
  "/admin",
  "/client",
  "/organization",
  "/analyst",
  "/senior",
  "/setup",
  "/change-password",
] as const;

export function workspaceLocaleScope(path: string): string | null {
  return AUTHENTICATED_PREFIXES.find(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  ) ?? null;
}

export function isAuthenticatedWorkspacePath(path: string): boolean {
  return workspaceLocaleScope(path) !== null;
}

export function applyWorkspaceLocale(locale: Parameters<typeof applyRuntimeLocale>[0]): void {
  applyRuntimeLocale(locale);
}

export function setWorkspaceLocaleReadyPath(path: string | null): void {
  if (path) document.documentElement.dataset.kavooshLocaleReadyPath = path;
  else delete document.documentElement.dataset.kavooshLocaleReadyPath;
}

/**
 * Resolves authenticated WS-L10N authority before feature effects start.
 * WorkspaceLocaleGate requires readiness for the authenticated workspace
 * boundary, not individual feature routes. Navigating between tabs within
 * the same workspace must not destroy the entire mounted application.
 */
export function WorkspaceLocaleRuntime({ path }: { path: string }) {
  const scope = workspaceLocaleScope(path);
  useEffect(() => {
    if (!scope) {
      setWorkspaceLocaleReadyPath(null);
      return;
    }

    let active = true;
    setWorkspaceLocaleReadyPath(null);
    applyWorkspaceLocale(FALLBACK_LOCALE);

    void Promise.all([listAvailableLocales(), getWorkspaceLocalePreferences()])
      .then(([inventory, preferences]) => {
        if (!active) return;
        const enabledLocales = inventory
          .filter((item) => item.installed && item.enabled)
          .map((item) => item.locale);
        const resolution = resolveRuntimeLocale({
          enabledLocales,
          userLocale: preferences.user_locale,
          tenantLocale: preferences.organization_default_locale,
          browserLanguages: navigator.languages,
        });
        applyWorkspaceLocale(resolution.locale);
        setWorkspaceLocaleReadyPath(scope);
      })
      .catch(() => {
        if (!active) return;
        applyWorkspaceLocale(FALLBACK_LOCALE);
        setWorkspaceLocaleReadyPath(scope);
      });

    return () => {
      active = false;
    };
  }, [scope]);

  return null;
}
